import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api.js';

/**
 * Feedback to the site admin, from the masthead.
 *
 * The name and the address are asked for rather than taken off the
 * account: the account name is a login, and the address on it may not be
 * the one somebody wants a reply at. The name starts as the account's
 * because that is the answer most of the time.
 *
 * The panel stays open on success and says the mail is away. Closing it
 * throws the text away, so nothing is cleared until it has been sent.
 *
 * A site with no mail refuses the submission outright, and the error it
 * gives is shown as it stands: better than a thank-you for something
 * nobody will receive.
 */

const MAX = 5000;

export function Feedback({ username, onClose }: { username: string; onClose: () => void }) {
  const [name, setName] = useState(username);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const canSubmit = message.trim() !== '' && message.length <= MAX
    && email.includes('@') && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.sendFeedback(name, email, message);
      setSent(true);
      setMessage('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be sent.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="help-backdrop" onClick={onClose} role="presentation">
      <div
        className="help-panel plate"
        role="dialog"
        aria-modal="true"
        aria-label="Submit feedback"
        tabIndex={-1}
        ref={panel}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hazard hazard-rule" role="presentation" />

        <div className="help-body">
          <div className="spread">
            <h3>Submit feedback</h3>
            <button className="secondary" onClick={onClose}>Close</button>
          </div>

          <div>
            <label htmlFor="feedback-name">Your name</label>
            <input
              id="feedback-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="feedback-email">Email address</label>
            <input
              id="feedback-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {/* Asked for so a reply has somewhere to go. It is not read
                off the account: that address may not be the one they
                want an answer at. */}
            <p className="mono muted" style={{ fontSize: '0.75rem', marginTop: '0.3rem' }}>
              Where a reply would go.
            </p>
          </div>

          <div>
            <label htmlFor="feedback-message">Feedback</label>
            <textarea
              id="feedback-message"
              rows={8}
              maxLength={MAX}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          {error && <p className="notice">{error}</p>}

          {sent && <p className="notice ok">Sent. Thank you.</p>}

          <div className="row">
            <button disabled={!canSubmit} onClick={() => { void submit(); }}>
              {busy ? 'Sending' : 'Send feedback'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
